import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconChevronRight from '@spothero/icons/chevron-right';
import IconChevronLeft from '@spothero/icons/chevron-left';
import IconUserInset from '@spothero/icons/user-inset';
import IconMobile from '@spothero/icons/mobile';
import {Button} from '@spothero/ui/backlog';
import DOMUtils from '@spothero/utils/dom';
import EnvironmentUtils from '@spothero/utils/environment';
import ExperimentUtils, {IMMEDIATE_FEATURES} from 'utils/experiment';
import UserUtils from 'utils/user-utils';
import AppStoreUtils from 'utils/app-store';
import Link from 'router/Link';

class NavigationCollapsed extends Component {
    static propTypes = {
        forceAnchors: PropTypes.bool,
        user: PropTypes.object,
        isOpen: PropTypes.bool,
        items: PropTypes.arrayOf(PropTypes.object).isRequired,
        onItemSelected: PropTypes.func,
        onCloseClick: PropTypes.func.isRequired,
    };
    state = {
        activeSecondaryMenu: null,
    };

    constructor(props) {
        super(props);

        const {items} = props;

        this._IS_IOS = EnvironmentUtils.isiOS();
        this._IS_ANDROID = EnvironmentUtils.isAndroid();
        this._secondaryMenus = [];

        items.forEach(secondary => {
            if (secondary.items) {
                this._secondaryMenus.push(secondary);

                secondary.items.forEach(tertiary => {
                    if (tertiary.items) {
                        this._secondaryMenus.push({
                            ...tertiary,
                            parentMenu: secondary.title,
                        });
                    }
                });
            }
        });
    }

    componentDidMount() {
        DOMUtils.addEventListener({
            event: 'click',
            target: '.NavigationCollapsed-item',
            handler: this._onNavMenuLinkClicked,
        });
    }

    componentWillUnmount() {
        DOMUtils.removeEventListener({
            event: 'click',
            target: '.NavigationCollapsed-item',
            handler: this._onNavMenuLinkClicked,
        });
    }

    _onNavMenuLinkClicked = evt => {
        const {onItemSelected, onCloseClick} = this.props;

        // if the item clicked has a secondary menu or its a secondary menu title, don't close
        if (
            DOMUtils.attr(evt.target, 'data-menu') ||
            DOMUtils.hasClass(evt.target, 'NavigationCollapsed-title')
        ) {
            return;
        }

        onCloseClick();

        if (onItemSelected) {
            onItemSelected(evt);
        }
    };

    _onDownloadLinkClicked = () => {
        if (!this._IS_ANDROID && !this._IS_IOS) {
            return;
        }

        const {
            user: {email},
        } = this.props;
        const args = {
            device: this._IS_IOS ? 'ios' : 'android',
            channel: 'prepurchase',
            ...(email && {email}),
        };

        AppStoreUtils.appStoreLinkClicked(args);
    };

    _onToggleSecondaryMenuClicked = evt => {
        evt.persist();
        evt.preventDefault();

        const menuToOpen = DOMUtils.attr(evt.currentTarget, 'data-menu');

        this.setState(prevState => {
            return {
                activeSecondaryMenu: !menuToOpen
                    ? null
                    : menuToOpen.replace(' ', ''),
            };
        });
    };

    closeSecondaryMenu() {
        this.setState({
            activeSecondaryMenu: null,
        });
    }

    _renderSecondaryMenuItem({key, item}) {
        const {forceAnchors} = this.props;
        const navItemClasses = classNames(
            'NavigationCollapsed-item',
            item.classes,
            {'NavigationCollapsed-item-alternate': item.alternate}
        );
        const nestedItems = item.items;
        const Tag = nestedItems ? 'a' : Link;
        const additionalProps = nestedItems
            ? {
                  href: '#',
                  'data-menu': item.title.replace(' ', ''),
                  onClick: this._onToggleSecondaryMenuClicked,
              }
            : {
                  to: item.url,
                  forceAnchor: forceAnchors,
              };

        return (
            <Tag key={key} className={navItemClasses} {...additionalProps}>
                {item.title}
                {nestedItems && (
                    <IconChevronRight className="NavigationCollapsed-item-action" />
                )}
            </Tag>
        );
    }

    _renderSecondaryMenu({key, menu}) {
        if (!menu.items) {
            return null;
        }

        const secondaryMenuClasses = classNames(
            'NavigationCollapsed',
            'NavigationCollapsed-secondary',
            'scrolling',
            {
                'NavigationCollapsed-open':
                    this.state.activeSecondaryMenu ===
                    menu.title.replace(' ', ''),
            }
        );

        return (
            <div
                key={`NavigationCollapsed-secondary-${key}`}
                className={secondaryMenuClasses}
            >
                <div className="NavigationCollapsed-items">
                    <span
                        className="NavigationCollapsed-item NavigationCollapsed-title"
                        onClick={this._onToggleSecondaryMenuClicked}
                        data-menu={menu.parentMenu}
                    >
                        <IconChevronLeft />
                        {menu.title}
                    </span>
                    {menu.items.map((item, j) => {
                        return this._renderSecondaryMenuItem({
                            key: j,
                            item,
                        });
                    })}
                </div>
            </div>
        );
    }

    render() {
        const {
            forceAnchors,
            user: {status, isAdmin, isSeller},
            isOpen,
            items,
        } = this.props;
        const classes = classNames(
            'NavigationCollapsed',
            'NavigationCollapsed-primary',
            'scrolling',
            {'NavigationCollapsed-open': isOpen}
        );
        const isLoggedIn = status === UserUtils.AUTH_STATE.USER;

        return (
            <div className={classes} role="navigation">
                <div className="NavigationCollapsed-items">
                    {isAdmin && (
                        <Link
                            className="NavigationCollapsed-item NavigationCollapsed-item-admin"
                            to="/admin"
                            forceAnchor
                        >
                            Admin
                        </Link>
                    )}
                    {isSeller && (
                        <Link
                            className="NavigationCollapsed-item NavigationCollapsed-item-cp"
                            to="/control-panel"
                            forceAnchor
                        >
                            Control Panel
                        </Link>
                    )}
                    {isLoggedIn ? (
                        <Link
                            className="NavigationCollapsed-item NavigationCollapsed-item-alternate"
                            to="/account-settings"
                            forceAnchor
                        >
                            <IconUserInset /> My Account
                        </Link>
                    ) : (
                        <Link
                            className="NavigationCollapsed-item NavigationCollapsed-item-alternate NavigationCollapsed-item-login"
                            to={
                                ExperimentUtils.isFeatureEnabled(
                                    IMMEDIATE_FEATURES.AUTH0_ROLLOUT_FEATURE
                                )
                                    ? '/auth/restricted-login'
                                    : '/login'
                            }
                            forceAnchor
                        >
                            <IconUserInset /> Log In or Sign Up
                        </Link>
                    )}
                    {items.map((item, i) => {
                        return this._renderSecondaryMenuItem({
                            key: i,
                            item,
                        });
                    })}
                    <Button
                        className="NavigationCollapsed-item"
                        onClick={this._onDownloadLinkClicked}
                    >
                        <IconMobile /> Download the App
                    </Button>
                    {isLoggedIn && (
                        <Link
                            className="NavigationCollapsed-item"
                            to="/logout"
                            forceAnchor={forceAnchors}
                        >
                            Log Out
                        </Link>
                    )}
                </div>
                {this._secondaryMenus.map((menu, i) => {
                    return this._renderSecondaryMenu({
                        key: i,
                        menu,
                    });
                })}
            </div>
        );
    }
}

export default NavigationCollapsed;
